import { type Configuration, LogLevel, PublicClientApplication } from '@azure/msal-browser'
import { API_BASE_URL } from './constants'

// MSAL configuration - will be loaded from backend
let msalConfig: Configuration | null = null
let msalInstance: PublicClientApplication | null = null

export interface AuthConfig {
  enabled: boolean
  client_id: string
  tenant_id: string
}

export async function initializeMsal(): Promise<PublicClientApplication | null> {
  try {
    // Fetch auth config from backend - use API_BASE_URL to match other API calls
    const response = await fetch(`${API_BASE_URL}/api/auth/config`)
    if (!response.ok) {
      // If auth config fails, assume auth is disabled and continue without it
      console.warn('[MSAL] Failed to fetch auth config (status:', response.status, ')- auth may be disabled')
      return null
    }

    let config: AuthConfig
    try {
      config = await response.json()
    } catch {
      console.error('[MSAL] Failed to parse auth config response')
      return null
    }

    if (!config.enabled) {
      console.log('[MSAL] Dashboard authentication is disabled')
      return null
    }

    if (!config.client_id || !config.tenant_id) {
      console.error('[MSAL] Missing client_id or tenant_id')
      return null
    }

    // Store any redirect parameter before MSAL takes over
    const searchParams = new URLSearchParams(window.location.search)
    const redirectPath = searchParams.get('redirect')
    if (redirectPath) {
      sessionStorage.setItem('auth_redirect_path', redirectPath)
    }

    msalConfig = {
      auth: {
        clientId: config.client_id,
        authority: `https://login.microsoftonline.com/${config.tenant_id}`,
        // Always use root URL as redirect URI (registered in Azure AD)
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
      },
      system: {
        loggerOptions: {
          loggerCallback: (level, message, containsPii) => {
            if (containsPii) return
            switch (level) {
              case LogLevel.Error:
                console.error('[MSAL]', message)
                break
              case LogLevel.Warning:
                console.warn('[MSAL]', message)
                break
              case LogLevel.Info:
                console.info('[MSAL]', message)
                break
              case LogLevel.Verbose:
                console.debug('[MSAL]', message)
                break
            }
          },
          logLevel: LogLevel.Warning,
        },
      },
    }

    msalInstance = new PublicClientApplication(msalConfig)
    await msalInstance.initialize()

    // Handle redirect promise (if returning from login)
    await msalInstance.handleRedirectPromise()

    return msalInstance
  } catch (error) {
    console.error('[MSAL] Initialization error:', error)
    return null
  }
}

export function getMsalInstance(): PublicClientApplication | null {
  return msalInstance
}

export const loginRequest = {
  scopes: ['User.Read'],
}

export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
}
